import { isPlatformBrowser } from '@angular/common';
import {
    APP_INITIALIZER,
    EnvironmentProviders,
    InjectionToken,
    Injector,
    makeEnvironmentProviders,
    Optional,
    PLATFORM_ID,
    SkipSelf
} from '@angular/core';
import { FirebaseApp, FirebaseApps, getApp, initializeApp } from '@angular/fire/app';
import { getMessaging, Messaging, MessagingInstances } from '@angular/fire/messaging';
import { App } from '@df/core/app';
import defaultsDeep from '@df/util/fn/defaults-deep';
import { DeepPartial } from '@df/util/type/deep-partial';
import { FirebasePermissionService } from './firebase-permission.service';
import { NFirebase, PROVIDED_FIREBASE_APPS, PROVIDED_MESSAGING_INSTANCES } from './firebase.interface';
import { FIREBASE_OPTIONS } from './firebase.options';
import { FirebaseService } from './firebase.service';

export function defaultFirebaseAppFactory(provided: FirebaseApp[] | undefined) {
    // Use the provided app, if there is only one, otherwise fetch the default app
    if (provided && provided.length === 1) {
        return provided[0];
    }

    return new FirebaseApp(getApp());
}

export function firebaseAppFactory(fn: (injector: Injector) => FirebaseApp) {
    return (injector: Injector) => {
        // For zoneless Angular, Firebase app initialization runs normally
        const app = fn(injector);

        return new FirebaseApp(app);
    };
}

// Injecting FirebaseApp will now only inject the default Firebase App
// this allows allows beginners to import /__/firebase/init.js to auto initialize Firebase App
// from the reserved URL.
const DEFAULT_FIREBASE_APP_PROVIDER = {
    provide: FirebaseApp,
    useFactory: defaultFirebaseAppFactory,
    deps: [[new Optional(), PROVIDED_FIREBASE_APPS]]
};

const FIREBASE_APPS_PROVIDER = {
    provide: FirebaseApps,
    deps: [[new Optional(), PROVIDED_FIREBASE_APPS]]
};

export function defaultMessagingInstanceFactory(provided: Messaging[] | undefined, defaultApp: FirebaseApp, platformId: Object) {
    if (!isPlatformBrowser(platformId)) {
        return null;
    }
    const defaultMessaging = provided && provided.length === 1 ? provided[0] : new Messaging(getMessaging(defaultApp));
    return defaultMessaging;
}

export function messagingInstanceFactory(fn: (injector: Injector, app: FirebaseApp) => Messaging) {
    return (injector: Injector, app: FirebaseApp) => {
        const platformId = injector.get(PLATFORM_ID);
        if (!isPlatformBrowser(platformId)) {
            return null;
        }

        // For zoneless Angular, messaging instance is created normally
        const messaging = fn(injector, app);

        return messaging;
    };
}

const MESSAGING_INSTANCES_PROVIDER = {
    provide: MessagingInstances,
    deps: [[new Optional(), PROVIDED_MESSAGING_INSTANCES]]
};

const DEFAULT_MESSAGING_INSTANCE_PROVIDER = {
    provide: Messaging,
    useFactory: defaultMessagingInstanceFactory,
    deps: [[new Optional(), PROVIDED_MESSAGING_INSTANCES], FirebaseApp, PLATFORM_ID]
};

export const FIREBASE_ROOT_GUARD = new InjectionToken<FirebaseService>('Internal Firebase ForRoot Guard');

export function createFirebaseRootGuard(firebase: FirebaseService): 'guarded' | TypeError {
    if (firebase) {
        throw new TypeError('Firebase providers already provided. Use provideFirebase() only once.');
    }

    return 'guarded';
}

/**
 * Provides Firebase configuration and initialization for the application
 */
export function provideFirebase(options: DeepPartial<NFirebase.IOptions> = {}): EnvironmentProviders {
    return makeEnvironmentProviders([
        FirebasePermissionService,
        FirebaseService,
        DEFAULT_FIREBASE_APP_PROVIDER,
        FIREBASE_APPS_PROVIDER,
        DEFAULT_MESSAGING_INSTANCE_PROVIDER,
        MESSAGING_INSTANCES_PROVIDER,
        {
            provide: FIREBASE_OPTIONS,
            useFactory: (app: App) => {
                const appConfig = app.dfConfig || ({} as any);
                const defaultOptions: DeepPartial<NFirebase.IOptions> = {
                    ...(appConfig.firebase || {})
                };

                return defaultsDeep(options || {}, defaultOptions);
            },
            deps: [App]
        },
        {
            provide: PROVIDED_FIREBASE_APPS,
            useFactory: firebaseAppFactory((injector: Injector) => {
                const firebaseOptions = injector.get(FIREBASE_OPTIONS);
                return initializeApp(firebaseOptions);
            }),
            multi: true,
            deps: [Injector]
        },
        {
            provide: PROVIDED_MESSAGING_INSTANCES,
            useFactory: messagingInstanceFactory((injector: Injector, app: FirebaseApp) => getMessaging(app)),
            multi: true,
            deps: [Injector, FirebaseApp]
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (firebaseService: FirebaseService, platformId: Object) => {
                return () => {
                    if (isPlatformBrowser(platformId)) {
                        firebaseService.init();
                        return Promise.resolve();
                    }
                    return null;
                };
            },
            multi: true,
            deps: [FirebaseService, PLATFORM_ID]
        },
        {
            provide: FIREBASE_ROOT_GUARD,
            useFactory: createFirebaseRootGuard,
            deps: [[FirebaseService, new Optional(), new SkipSelf()]]
        }
    ]);
}
